/**
 * A Regional Director has exactly one region and is its director, so the
 * region picker narrows to one region that nobody else directs.
 */
import { regionsOpenToDirector, type RegionOption } from '../../../shared/regionAccessOptions';

import type { UserRoleId } from './roleTaxonomy';

export function includesDirectorRole(roleIds: UserRoleId[]): boolean {
  return roleIds.includes('regional-director');
}

/** The regions offered for these roles. `userId` is the person being edited,
 *  or null for a brand-new invitee. */
export function regionChoicesFor(
  roleIds: UserRoleId[],
  options: RegionOption[],
  userId: string | null,
): RegionOption[] {
  return includesDirectorRole(roleIds) ? regionsOpenToDirector(options, userId) : options;
}

/** Drop picks the new roles no longer allow, keeping at most one for a Regional Director. */
export function fitRegionsToRoles(
  roleIds: UserRoleId[],
  regionIds: string[],
  choices: RegionOption[],
): string[] {
  if (!includesDirectorRole(roleIds)) return regionIds;
  const allowed = new Set(choices.map((c) => c.id));
  return regionIds.filter((id) => allowed.has(id)).slice(0, 1);
}

/** The region picks that survive a change to `roleIds`. */
export function regionsAfterRoleChange(
  roleIds: UserRoleId[],
  regionIds: string[],
  options: RegionOption[],
  userId: string | null,
): string[] {
  return fitRegionsToRoles(roleIds, regionIds, regionChoicesFor(roleIds, options, userId));
}

/** Whether the picked regions satisfy the Regional Director's one-region rule. */
export function directorRegionsValid(roleIds: UserRoleId[], regionIds: string[]): boolean {
  return !includesDirectorRole(roleIds) || regionIds.length === 1;
}

export const DIRECTOR_REGION_HINT =
  "A Regional Director has one region and becomes its director. Regions that already have a director aren't listed.";
